import { isDiscreteStop, legendConfigApi } from 'api-validations/stitch'

import { toQuery, useApiClient, useQuery } from '../../api-provider'
import { useStitchRequestType } from '../../field-data'
import { useAsAppliedLayerOrNull } from '../map-state-store'
import { type ReconcileResult, reconcileDiscreteLegends } from './reconcileDiscreteLegends'

/**
 * The result of fetching and reconciling discrete legend colors across merged streams.
 *
 * Includes the reconciliation output (reconciled stops, per-stream remapped colors,
 * conflict flag) plus a loading/ready flag to gate rendering.
 */
export type ReconciledDiscreteColors = ReconcileResult

const empty: ReconciledDiscreteColors = {
  hasConflicts: false,
  remappedColors: new Map(),
  reconciledStops: []
}

/**
 * Fetches the discrete legend config for every stream in the current merge set
 * (primary + all mergedStreamIds) in parallel, then reconciles them so that:
 *
 *   - Each unique label has a single canonical color across all streams
 *   - The primary stream's color assignments are preserved unchanged
 *   - Conflicting labels in secondary streams get colors from the supplemental palette
 *
 * Only meaningful for discrete layers. Pass isDiscrete=false to skip fetching entirely.
 */
export const useReconciledDiscreteColors = ({
  orgCode,
  isDiscrete,
  isComparison = false
}: {
  orgCode: string
  isDiscrete: boolean
  isComparison?: boolean
}): ReconciledDiscreteColors => {
  // Tolerates a non-as-applied selection (plan asset / plan editor): no layer
  // means nothing to reconcile, so the query stays disabled.
  const layer = useAsAppliedLayerOrNull(isComparison)
  const { streamId, mergedStreamIds, applicationId, layerId } = layer ?? {
    streamId: '',
    mergedStreamIds: [],
    applicationId: '',
    layerId: ''
  }
  const apiClient = useApiClient()
  const requestType = useStitchRequestType()
  // Stream IDs are base64-encoded protobuf, guaranteed not to contain commas,
  // so joining them produces a stable primitive for the query key.
  const streamIdKey = [streamId, ...mergedStreamIds].join(',')

  const query = useQuery(
    toQuery({
      queryKey: [
        'merged-legend-configs',
        streamIdKey,
        orgCode,
        applicationId,
        layerId,
        requestType
      ],
      enabled: isDiscrete && layer !== null,
      queryFn: async () => {
        const streamIds = streamIdKey.split(',')
        const results = await Promise.allSettled(
          streamIds.map((id) =>
            apiClient.callApi(legendConfigApi, {
              orgCode,
              streamId: id,
              applicationId,
              layerId,
              requestType
            })
          )
        )
        // Use allSettled so a single-stream failure degrades gracefully
        // (partial reconciliation) rather than losing all cross-stream color mapping.
        const streams = results.flatMap((result, i) =>
          result.status === 'fulfilled'
            ? [
                {
                  streamId: streamIds[i],
                  discreteColors: result.value.legend.colors.filter(isDiscreteStop)
                }
              ]
            : []
        )
        return reconcileDiscreteLegends(streams)
      }
    })
  )

  if (!query.isSuccess) return empty

  return query.data
}
